import type { AnswerAndData } from 'shared/definitions/answer-and-data';
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import * as Notation from '../notation';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import SkillCalculator from '../uTheory.Skills/skillCalculator';
import utils from '../utils';
import type { Note, Interval } from '../notation';

interface IntervalInversionOptions extends InteractiveOptions {
  highestNote?: Note | string;
  lowestNote?: Note | string;
}

export type IntervalInversionQuestion = {
  interval: Interval;
  answer: Interval;
  text: [string, number];
  skills: string;
};

class IntervalInversion extends InteractivePrototype {
  declare questions: IntervalInversionQuestion[];

  declare currentQuestion: IntervalInversionQuestion;

  declare options: IntervalInversionOptions;

  skillCalculator!: SkillCalculator;

  maxQuestions!: number;

  answer: {
    intervalQuality: string;
    intervalName: string;
  };

  answerDiv!: HTMLDivElement;

  answerAttempts: number;

  constructor(displayDiv: HTMLDivElement, options: IntervalInversionOptions) {
    super(displayDiv, options);

    this.answerAttempts = 0;
    this.targetSpeedFromSkills = IntervalInversion.registry.skills;
    this.settings = {
      instructions: {
        ariaLive: 'alert',
        ariaLiveRegion: 0,
      },
      notation: false,
      letters: {
        numberButtons: true,
        intervalQualityButtons: true,
        checkButton: true,
      },
      checkMark: true,
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    this.answer = {
      intervalQuality: '',
      intervalName: '',
    };

    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: IntervalInversionOptions): Promise<any> {
    if (options.lowestNote === undefined) {
      options.lowestNote = 'C2';
    }

    if (options.highestNote === undefined) {
      options.highestNote = 'C6';
    }

    this.skillCalculator = new SkillCalculator();
    return super.init(displayDiv, options);
  }

  generateQuestions(questions: string[]): void {
    const options = this.options;
    this.questions = options.drill
      ? this.generateDrillQuestions(options)
      : this.generateManualQuestions(questions);

    this.maxQuestions = this.questions.length;
    this.questionPrompt = 'What is the inversion of {question}?';
    if (!options.testMode) {
      this.questionPrompt += `<br/><small>#{question} of ${this.questions.length}</small>`;
    }
  }

  generateManualQuestions(questions: string[]): IntervalInversionQuestion[] {
    questions = questions || this.options.questions;

    return questions.map((question: string, index: number): IntervalInversionQuestion => {
      const interval = new Notation.Interval(question as IntervalName);
      const inversion = interval.getInversion();
      const skills = this.calculateSkill(interval);

      return {
        interval,
        answer: inversion,
        text: [this.getIntervalText(interval), index + 1],
        skills,
      };
    }, this);
  }

  generateDrillQuestions(options: IntervalInversionOptions): IntervalInversionQuestion[] {
    const generator = new QuestionGenerator();
    const maxQuestions = options.maxQuestions || 10;

    return generator.interval_inversion(maxQuestions).map((question, index) => {
      const interval = question.interval;
      const inversion = interval.getInversion();
      const skills = 'interval_inversion';

      return {
        interval,
        answer: inversion,
        text: [this.getIntervalText(interval), index + 1],
        skills,
      };
    }, this);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, class-methods-use-this
  calculateSkill(interval: Interval): string {
    return 'interval_inversion';
  }

  // eslint-disable-next-line class-methods-use-this
  initializeMusic(): void {}

  createCustomDivs(): void {
    this.createAnswerDiv();
  }

  createAnswerDiv(): void {
    this.answerDiv = document.createElement('div');

    const div = this.answerDiv;
    const styleOptions = {
      fontSize: '4vw',
      fontWeight: 'bold',
      textAlign: 'center',
      padding: '10px',
    };

    div.id = 'answerDiv';
    div.innerHTML = '?';

    utils.merge(div.style, styleOptions);

    this.displayDiv.insertBefore(div, this.displayDiv.children[1]!);
  }

  newQuestion(): void {
    this.progressTracker!.start(this.targetSpeed);

    super.newQuestion();
    console.log('= NEW QUESTION =', this.currentQuestion);
  }

  // eslint-disable-next-line class-methods-use-this
  getIntervalText(interval: Interval): string {
    const intervalText = interval.longName();
    const aOrAn = utils.aOrAn(intervalText);

    return `${aOrAn} <strong>${intervalText}</strong>`;
  }

  newCheckpointQuestion(questionString: string): Promise<IntervalInversionQuestion> {
    return this.loadingPromise.then((): Promise<IntervalInversionQuestion> => {
      this.questionNumber = 0;
      this.generateQuestions([questionString]);
      this.newQuestion();
      this.resize();
      return Promise.resolve(this.currentQuestion);
    });
  }

  addListeners(): void {
    this.letterPanel!.addNumberCallback(this.addIntervalNameToAnswer, this);
    this.letterPanel!.addIntervalQualityCallback(this.addIntervalQualityToAnswer, this);
    this.letterPanel!.addCheckCallback(this.checkAnswer, this);
  }

  addIntervalNameToAnswer(semitones: string | number): void {
    if (typeof semitones === 'number') {
      semitones = `${semitones}`;
    }
    this.answer.intervalName = semitones;

    return this.displayAnswer();
  }

  addIntervalQualityToAnswer(quality: string | number): void {
    if (typeof quality === 'number') {
      quality = `${quality}`;
    }
    this.answer.intervalQuality = quality;

    return this.displayAnswer();
  }

  displayAnswer(): void {
    const answer = this.answer.intervalQuality + this.answer.intervalName;

    if (answer === '') {
      this.answerDiv!.removeAttribute('aria-live');
      this.answerDiv!.innerHTML = '?';
    } else {
      this.answerDiv!.setAttribute('aria-live', 'polite');
      this.answerDiv!.innerHTML = answer;
    }
  }

  checkAnswer(): void {
    if (this.rightAnswerDelay) return;
    this.answerAttempts++;
    const answer = this.questions[this.questionNumber]!.answer;

    const testMode = this.optionsTrue('testMode');
    const questionAnswer = answer.quality + answer.genericInterval;
    const userAnswer = this.answer.intervalQuality + this.answer.intervalName;
    const correct = questionAnswer === userAnswer;
    console.log(
      `checkAnswer(): expects ${questionAnswer} and received ${userAnswer} (${
        correct ? 'CORRECT' : 'INCORRECT'
      })`
    );

    if (testMode === true) {
      this.checkpointAnswer(correct);
      this.clearAnswer();
      return;
    }

    if (!this.answer.intervalQuality) {
      this.checkMark!.warn("What is it's quality?");
      return;
    }
    if (!this.answer.intervalName) {
      this.checkMark!.warn("What is it's number?");
      return;
    }

    if (correct === true) {
      this.getAnswerAndData(true);
      this.rightAnswer(1800);
      window.setTimeout(this.clearAnswer.bind(this), 1800);
    } else {
      this.getAnswerAndData(false);
      this.wrongAnswer();
    }
  }

  clearAnswer(): void {
    this.answer = {
      intervalName: '',
      intervalQuality: '',
    };
    this.displayAnswer();
  }

  getAnswerAndData(correct?: boolean): AnswerAndData {
    const correctAnswer = this.questions[this.questionNumber]!.answer;
    const question = this.questions[this.questionNumber]!.interval;

    const answerAndData = {
      question: `${question.toString()}`,
      questionType: 'IntervalInversion',
      questionText: this.instructionsDiv!.innerHTML,
      questionSVG: undefined,
      studentAnswerText: `${this.answer.intervalQuality}${this.answer.intervalName}`,
      correctAnswerText: `${correctAnswer}`,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: [this.questions[this.questionNumber]!.skills],
    };

    return answerAndData;
  }

  gameOver(): void {
    this.instructionsDiv!.innerHTML = 'Nice work!';
    this.instructionsDiv!.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv!);
    this.displayDiv.removeChild(this.answerDiv!);

    super.gameOver();
  }

  playAnswer(): void {
    const direction = [-1, 1][Math.floor(Math.random() * 2)] as 1 | -1;
    const interval = this.currentQuestion.interval;
    const inversion = this.currentQuestion.answer;
    interval.setDirection(direction);
    inversion.setDirection((direction * -1) as 1 | -1);
    const note = Notation.Note.randomNote({
      highest: 'G5',
      lowest: 'G2',
    });
    this.player!.stop();
    this.player!.play(note, { startTime: 0, stopTime: 0.4 });
    this.player!.play(note.getNoteAt(interval) as Note, { startTime: 0.4, stopTime: 0.8 });
    this.player!.play(note, { startTime: 0.8, stopTime: 1.2 });
    this.player!.play(note.getNoteAt(inversion) as Note, { startTime: 1.2, stopTime: 1.8 });
  }

  static registry: Record<string, any> = {
    description: 'Interval Inversion',
    skills: ['interval_inversion'],
  };
}

export default IntervalInversion;
